import { useState, useEffect, useCallback, useRef } from 'react';
import { useStreamChat } from '../contexts/StreamContext';
import { Sentry } from '../lib/sentry';
import { joinShowChat, getPublicProfile } from '../api/client';
import type { Channel, Event } from 'stream-chat';

export interface ChatMessage {
  id: string;
  senderId: string | null;
  senderName: string;
  senderAvatar: string | null;
  content: string;
  isLocal: boolean;
}

export function useLiveChatMessages(channelId: string | null, showId?: string) {
  const { client, isReady } = useStreamChat();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [joined, setJoined] = useState(false);
  const channelRef = useRef<Channel | null>(null);
  const avatarCacheRef = useRef<Map<string, string | null>>(new Map());

  // Resolve avatar: prefer Stream Chat's user.image (set by backend), fall back to profile API
  const resolveAvatar = useCallback((userId: string, username: string, streamImage?: string): string | null => {
    if (streamImage) {
      avatarCacheRef.current.set(userId, streamImage);
      return streamImage;
    }

    const cache = avatarCacheRef.current;
    if (cache.has(userId)) return cache.get(userId) || null;

    cache.set(userId, null); // mark pending
    getPublicProfile(username).then((profile) => {
      const url = profile.avatarUrl || null;
      cache.set(userId, url);
      if (url) {
        setMessages(prev =>
          prev.map(msg =>
            msg.senderName === username && !msg.senderAvatar
              ? { ...msg, senderAvatar: url }
              : msg
          )
        );
      }
    }).catch(() => {});
    return null;
  }, []);

  // Join the show chat channel and load initial messages
  useEffect(() => {
    if (!client || !isReady || !channelId) return;

    let cancelled = false;

    async function joinAndLoad() {
      const loadStart = performance.now();
      try {
        // Tell backend to add user to the channel
        if (showId) {
          await joinShowChat(showId).catch((err) => {
            console.warn('[ShowChat] Backend join failed:', err);
          });
        }

        const channel = client!.channel('livestream', channelId!);
        await channel.watch();

        if (cancelled) return;

        channelRef.current = channel;
        setJoined(true);

        // Load existing messages
        const existingMessages = (channel.state.messages || [])
          .filter(msg => msg.text)
          .map(msg => {
            const username = msg.user?.name || msg.user?.id || 'Anonymous';
            return {
              id: msg.id,
              senderId: msg.user?.id || null,
              senderName: username,
              senderAvatar: msg.user?.id ? resolveAvatar(msg.user.id, username, msg.user?.image as string | undefined) : null,
              content: msg.text!,
              isLocal: msg.user?.id === client!.userID,
            };
          });
        setMessages(existingMessages);

        const loadDuration = Math.round(performance.now() - loadStart);
        Sentry.addBreadcrumb({ category: 'stream', message: `Channel loaded in ${loadDuration}ms`, level: 'info', data: { loadDurationMs: loadDuration } });
      } catch (err) {
        console.error('[ShowChat] Failed to join channel:', err);
        Sentry.captureException(err, { tags: { component: 'stream', operation: 'channel_join' } });
      }
    }

    joinAndLoad();
    return () => {
      cancelled = true;
      if (channelRef.current) {
        channelRef.current.stopWatching().catch(() => {});
        channelRef.current = null;
      }
    };
  }, [client, isReady, channelId]);

  // Listen for new messages
  useEffect(() => {
    if (!channelRef.current || !joined) return;

    const channel = channelRef.current;
    const handler = channel.on('message.new', (event: Event) => {
      const msg = event.message;
      if (!msg || !msg.text) return;

      const username = msg.user?.name || msg.user?.id || 'Anonymous';
      const newMsg: ChatMessage = {
        id: msg.id,
        senderId: msg.user?.id || null,
        senderName: username,
        senderAvatar: msg.user?.id ? resolveAvatar(msg.user.id, username, msg.user?.image as string | undefined) : null,
        content: msg.text,
        isLocal: msg.user?.id === client?.userID,
      };

      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
    });

    return () => { handler.unsubscribe(); };
  }, [client, joined, resolveAvatar]);

  // Send a message
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || !channelRef.current || !joined) return;
    try {
      await channelRef.current.sendMessage({ text: text.trim() });
    } catch (e) {
      console.error('[ShowChat] Failed to send message:', e);
      Sentry.captureException(e, { tags: { component: 'stream', operation: 'send_message' } });
    }
  }, [joined]);

  return {
    messages,
    isJoined: joined,
    sendMessage,
  };
}
